import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  type Signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { type ConnectedPosition } from '@angular/cdk/overlay';
import { MatIconModule } from '@angular/material/icon';
import { CustomTooltipModule } from '@principle-theorem/ng-shared';
import { CurrentBrandScope } from '@principle-theorem/ng-principle-shared';
import { type IDepositMetadata } from '@principle-theorem/principle-core/interfaces';
import {
  DepositChipDisplay,
  type IDepositAbsence,
  type IDepositChipState,
} from './deposit-chip-display';
import { DepositChipTooltipComponent } from './deposit-chip-tooltip.component';

/**
 * The single chip standing in for a deposit wherever one is listed.
 *
 * Takes the deposit's own metadata and derives its own copy and colour, so a
 * table only has to say which deposit it is showing. Rows with no deposit pass
 * an {@link IDepositAbsence} instead, keeping one visual language across a
 * column rather than a chip in some rows and bare text in others.
 *
 * The status and amount run along one line and wrap only when the space runs
 * out. An earlier version forced the amount onto a second row always, which
 * bought room the chip rarely needed and made every row in a column twice as
 * tall.
 *
 * Only the text wraps. The hold-or-charge icon and any marker sit outside that
 * flow, down the chip's edges and centred against however many lines the text
 * takes — letting them join the wrap left the icon stranded alone on the first
 * line with the status beneath it. The minimum width is the floor below which
 * the longest status breaks into three lines and stops being scannable.
 */
@Component({
  selector: 'pr-deposit-status-chip',
  templateUrl: './deposit-status-chip.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIconModule, CustomTooltipModule, DepositChipTooltipComponent],
  host: { class: 'inline-flex max-w-full' },
})
export class DepositStatusChipComponent {
  readonly depositsEnabled = toSignal(
    inject(CurrentBrandScope).depositsEnabled$,
    {
      initialValue: false,
    }
  );
  readonly deposit = input<IDepositMetadata>();
  readonly absence = input<IDepositAbsence>();
  /**
   * Suppresses the hover card where one would be in the way — inside an
   * autocomplete panel, a tooltip over the option being chosen fights the
   * list it belongs to. The chip is otherwise unchanged, because a deposit
   * should not read differently depending on where it is being shown.
   */
  readonly tooltipDisabled = input(false, { transform: coerceBooleanProperty });

  /**
   * Below the chip first, so a tooltip never covers the row it describes.
   *
   * Each edge is offered left-aligned and then right-aligned. The overlay is
   * absolutely positioned from whichever edge it is aligned to, so a
   * left-aligned tooltip on a chip at the right of the page only has the
   * space between the chip and the viewport edge to lay itself out in and
   * wraps into a sliver. Offering the right-aligned fallback lets the CDK
   * pick it whenever the left-aligned one would run off the page.
   */
  readonly positions: ConnectedPosition[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top' },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom' },
    { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom' },
  ];

  /** Undefined only when neither input is set, which renders nothing. */
  readonly state: Signal<IDepositChipState | undefined> = computed(() => {
    if (!this.depositsEnabled()) {
      return;
    }
    const deposit = this.deposit();
    if (deposit) {
      return DepositChipDisplay.forMetadata(deposit);
    }
    const absence = this.absence();
    return absence ? DepositChipDisplay.forAbsence(absence) : undefined;
  });
}
